import type { Branding } from '@logto/schemas';
import { Theme } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';

import defaultDarkLogo from '@/shared/assets/icons/jx-logo-dark.svg?url';
import defaultLogo from '@/shared/assets/icons/jx-logo-light.svg?url';

export type GetLogoUrl = {
  theme: Theme;
  logoUrl: string;
  darkLogoUrl?: Nullable<string>;
};

export const getLogoUrl = ({ theme, logoUrl, darkLogoUrl }: GetLogoUrl) => {
  if (theme === Theme.Dark) {
    return darkLogoUrl ?? logoUrl;
  }

  return logoUrl;
};

export type GetBrandingLogoUrl = {
  theme: Theme;
  branding: Branding;
  isDarkModeEnabled: boolean;
  fallbackToDefault?: boolean;
};

export const getBrandingLogoUrl = ({
  theme,
  branding,
  isDarkModeEnabled,
  fallbackToDefault = true,
}: GetBrandingLogoUrl) => {
  // Only replace the upstream seed assets; customer-hosted branding stays intact.
  const resolveLogo = (url: Nullable<string> | undefined) =>
    url === 'https://logto.io/logo.svg'
      ? defaultLogo
      : url === 'https://logto.io/logo-dark.svg'
        ? defaultDarkLogo
        : url;
  const logoUrl = resolveLogo(branding.logoUrl);
  const darkLogoUrl = resolveLogo(branding.darkLogoUrl);
  const fallbackLogo = fallbackToDefault
    ? isDarkModeEnabled && theme === Theme.Dark
      ? defaultDarkLogo
      : defaultLogo
    : null;

  if (!isDarkModeEnabled) {
    return logoUrl ?? fallbackLogo;
  }

  if (!logoUrl && !darkLogoUrl) {
    return fallbackLogo;
  }

  if (logoUrl && darkLogoUrl) {
    return getLogoUrl({ theme, logoUrl, darkLogoUrl });
  }

  return logoUrl ?? darkLogoUrl;
};
